/**
 * ClientBriefsSection
 *
 * The in-production briefs surface at the top of the client portal home. Lists
 * the client's CAPTURING + EDITING briefs (the production queue the portal
 * endpoint returns); tapping one navigates to its brief page
 * (`ClientBriefRoute`) with a stamped breadcrumb trail.
 *
 * Rendered as progressive enhancement: it returns null while the query is in
 * flight and when the client has no briefs in production, so the home page
 * never shows an empty or spinner-only briefs card.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { usePortalBriefsQuery } from "@/domains/Briefs/hooks/useBriefs";
import { BRIEF_STATUS_CONFIG } from "@/domains/Briefs/briefStatusConfig";
import { ChevronRightIcon } from "@/components/icons";
import { useLocale } from "@/hooks/useLocale";
import { useTrailNavOptions } from "@/core/navigation/trail";

interface ClientBriefsSectionProps {
  clientId: string;
}

export const ClientBriefsSection: React.FC<ClientBriefsSectionProps> = ({
  clientId,
}) => {
  const { t } = useLocale();
  const navigate = useNavigate();
  // Stamp the dashboard as the first crumb so the brief page reads
  // "Home > [Brief title]".
  const trailNav = useTrailNavOptions(t("client.trail.dashboard"));
  const { data: briefs, isLoading } = usePortalBriefsQuery(clientId);

  // Progressive enhancement: no card at all until we know there are briefs to
  // show. Avoids a spinner flash for a section that may not exist.
  if (isLoading || !briefs || briefs.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl">
      <div className="p-4">
        <h2 className="text-lg font-semibold text-gray-900">
          {t("portal.briefs.title")}
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          {t("portal.briefs.subtitle")}
        </p>
      </div>

      <div className="p-4 pt-0 space-y-3">
        {briefs.map((brief) => {
          const status = BRIEF_STATUS_CONFIG[brief.status];
          const ideaLabel =
            brief.ideaCount === 1
              ? t("portal.briefs.ideasOne")
              : t("portal.briefs.ideasMany");
          return (
            <button
              key={brief.id}
              type="button"
              onClick={() =>
                navigate(`/client/${clientId}/briefs/${brief.id}`, trailNav)
              }
              className="w-full flex items-center justify-between gap-3 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors text-left group"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {brief.title || "Brief"}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${status.bg} ${status.text}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                    {status.label}
                  </span>
                  <span className="text-xs text-gray-500">
                    {brief.ideaCount} {ideaLabel}
                  </span>
                </div>
              </div>
              <ChevronRightIcon className="w-5 h-5 shrink-0 text-gray-400 group-hover:text-gray-600 transition-colors" />
            </button>
          );
        })}
      </div>
    </div>
  );
};
